"use client";

import { useEffect, useState } from "react";
import Preloader from "./preloader";
import { SidebarInset, SidebarProvider } from "./ui/sidebar";
import { DocsAccountControls } from "@/providers/account-session";
import { DocsGuestRail } from "./docs/guest-rail";
import { useBootReady } from "@/lib/boot-state";
import { cn } from "@/lib/utils";

/**
 * The ONE preloader instance for the whole session (spec §2). Mounted as a
 * stable root sibling by AccountSessionProvider so the Lottie animation
 * loads exactly once — the app tree assembles and paints UNDERNEATH this
 * loading shell with an interactive account rail. Protected content remains
 * behind the readiness gate; public navigation stays available. When the boot
 * store flips ready the overlay fades out
 * (300ms) and unmounts for good (ready is sticky, so it can never return).
 */
export default function BootOverlay() {
  const ready = useBootReady();
  const [gone, setGone] = useState(false);

  // Unmount fallback for environments where transitionend never fires
  // (prefers-reduced-motion disables the transition; jsdom has no CSS engine).
  useEffect(() => {
    if (!ready || gone) return;
    const timer = setTimeout(() => setGone(true), 600);
    return () => clearTimeout(timer);
  }, [ready, gone]);

  if (gone) return null;

  return (
    <div
      data-testid="boot-overlay"
      className={cn(
        "fixed inset-0 z-40 bg-background transition-opacity duration-300 ease-out",
        ready && "pointer-events-none opacity-0",
      )}
      onTransitionEnd={(event) => {
        if (event.target === event.currentTarget && ready) setGone(true);
      }}
    >
      <SidebarProvider className="h-svh overflow-hidden">
        <DocsAccountControls>
          <DocsGuestRail />
        </DocsAccountControls>
        <SidebarInset className="relative overflow-hidden">
          <div role="status" aria-label="Loading account">
            <Preloader />
          </div>
        </SidebarInset>
      </SidebarProvider>
    </div>
  );
}
